import React from "react";
import PropTypes from "prop-types";

import LightSelect from "../../CompanyView/LightSelect";
import MultiSelect from "./MultiSelect";
import InfoTip from "./InfoTip";
import { UseCompanyWorkspaces, UseSelectedWorkspace } from "../../../hooks/company";
import { WINDOWS } from "../../../helpers/scopedWindows";
import { companyTodayStr } from "../../../helpers/companyTime";
import "./customize.css";

/**
 * Scope · Window · Show — the settings every cross-workspace component needs,
 * written once so they cannot drift into three different date pickers and three
 * different workspace lists.
 *
 *   WORKSPACES  tick-box dropdown; two or more puts a dropdown on the CARD
 *   ADMIN VIEW  whether that card dropdown lists workspaces or players
 *   SHOW        average per player, or the team total
 *   DATE RANGE  this month · quarter · YTD · a range typed by hand
 *
 * The help lives on ⓘ, not in paragraphs under each control — four fields with
 * four explanations underneath read as a wall of prose and buried the settings.
 */

const Pick = ({ value, options, onChange }) => (
  <LightSelect
    width="100%"
    height={36}
    fontSize={12.5}
    radius={8}
    menuWidth={336}
    value={value}
    options={options}
    onChange={(o) => onChange(o.value)}
  />
);
Pick.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
};

const Field = ({ label, tip, children }) => (
  <div className="gmbw-fld">
    <label>
      {label}
      <InfoTip text={tip} />
    </label>
    {children}
  </div>
);
Field.propTypes = {
  label: PropTypes.string.isRequired,
  tip: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

const ScopeWindow = ({ draft, onChange, fields }) => {
  const show = (name) => !fields || fields.includes(name);
  const workspaces = UseCompanyWorkspaces(true) || [];
  const selected = UseSelectedWorkspace();
  const picked = (draft.workspaceIds || []).map(String);
  const win = draft.window || "follow";
  const custom = draft.customRange || {};

  const set = (patch) => onChange({ ...draft, ...patch });

  const toggleWs = (id) => {
    const next = picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id];
    /* Keep them in the company's own order, and never leave the card's own
       dropdown pointing at a workspace that is no longer in scope. */
    const ordered = workspaces.map((w) => String(w._id)).filter((x) => next.includes(x));
    const host = draft.workspaceId != null ? String(draft.workspaceId) : "";
    set({
      workspaceIds: ordered,
      cardPick: ordered.includes(draft.cardPick) ? draft.cardPick : "all",
      /* If a leftover single workspaceId isn't in the multi pick, snap it to
         the first selected team so host fetches don't read a third workspace. */
      ...(ordered.length && host && !ordered.includes(host)
        ? { workspaceId: ordered[0] }
        : {}),
    });
  };

  return (
    <>
      {show("workspaces") && (
        <div className="gmbw-scopehead">
          <div className="gmbw-scopehead-t">Admin view</div>
          <p className="gmbw-scopehead-s">Players always see their own numbers. This is what you see as an admin.</p>
        </div>
      )}
      {show("workspaces") && (
      <Field
        label="Which workspaces to track"
        tip="One workspace = that team’s average. Tick more and the card combines them (average of each team’s average, or total — set below). You do not pick a single workspace again afterward."
      >
        <MultiSelect
          options={workspaces.map((w) => ({ value: String(w._id), label: w.name }))}
          selected={picked}
          onToggle={toggleWs}
          emptyLabel={selected?.name ? `${selected.name} (this board’s own)` : "This board’s own workspace"}
        />
        {picked.length >= 2 && (
          <p className="gmbw-field-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2 2 7l10 5 10-5-10-5Z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
            </svg>
            <span>
              Tracking {picked.length} teams <b>together</b> on this card. Optional: use the board <b>Viewing</b> filter to look at one team at a time. Only teams that track a metric count toward its average.
            </span>
          </p>
        )}
      </Field>
      )}

      {show("admin") && (
      <Field
        label="Card dropdown shows"
        tip="What the dropdown on the card lists: the workspaces ticked above, or every player inside them so you can pull up one person."
      >
        <Pick
          value={draft.filterBy === "player" ? "player" : "workspace"}
          options={[
            { value: "workspace", label: "Workspaces" },
            { value: "player", label: "Players" },
          ]}
          onChange={(v) => set({ filterBy: v, cardPick: "all" })}
        />
      </Field>
      )}

      {show("show") && (
      <Field
        label="Show the team as"
        tip="Average divides by the players who actually scored on each metric — the same rule the app already uses for an admin. Total sums everyone."
      >
        <Pick
          value={draft.avgMode === "total" ? "total" : "avg"}
          options={[
            { value: "avg", label: "Average per player" },
            { value: "total", label: "Team total" },
          ]}
          onChange={(v) => set({ avgMode: v })}
        />
      </Field>
      )}

      {show("range") && (
      <Field
        label="Date range"
        tip="The range this card falls back to. The dashboard's own time filter in the top bar always wins while it is set, so every card on a board moves together — this is what the card reads when the board has no filter of its own. Custom range lets you type your own dates; leave the end empty to run up to today. To give one metric its own window regardless of the board, use the per-metric date filter under Metrics."
      >
        <Pick
          value={win}
          options={[{ value: "follow", label: "Match the dashboard" }, ...WINDOWS.map((w) => ({ value: w.id, label: w.label }))]}
          onChange={(v) => set({ window: v })}
        />
        {win === "custom" && (
          <div className="gmbw-range">
            <input
              type="date"
              aria-label="From"
              max={companyTodayStr()}
              value={custom.from || ""}
              onClick={(e) => { try { e.currentTarget.showPicker(); } catch (_) { /* older browser */ } }}
              onChange={(e) => set({ customRange: { ...custom, from: e.target.value } })}
            />
            <span className="gmbw-range-sep">→</span>
            <input
              type="date"
              aria-label="To"
              max={companyTodayStr()}
              min={custom.from || undefined}
              value={custom.to || ""}
              onClick={(e) => { try { e.currentTarget.showPicker(); } catch (_) { /* older browser */ } }}
              onChange={(e) => set({ customRange: { ...custom, to: e.target.value } })}
            />
          </div>
        )}
      </Field>
      )}
    </>
  );
};

ScopeWindow.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  /** Which controls to render; omit for all four. */
  fields: PropTypes.arrayOf(PropTypes.string),
};
ScopeWindow.defaultProps = {
  fields: null,
};

export default ScopeWindow;
